import { useEffect, useMemo, useState } from 'react'
import { AdminLayout, useAdminAccess } from '../../components/AdminLayout'
import { useLanguage } from '../../contexts/LanguageContext'
import { requestApi } from '../../lib/adminApi'

type AdminUser = {
  id: string
  username?: string | null
  phone?: string | null
  email?: string | null
  is_admin: boolean
  admin_notes?: string | null
  status: string
  plan: string
  credits: number
  trial_credits_remaining: number
  created_at?: string | null
  last_login_at?: string | null
}

type UserListResponse = {
  users: AdminUser[]
  total: number
  limit: number
  offset: number
}

type EditDraft = {
  status: string
  plan: string
  is_admin: boolean
  notes: string
}

const AdminUsersContent = () => {
  const { t } = useLanguage()
  const { isGranted } = useAdminAccess()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [adminFilter, setAdminFilter] = useState('all')
  const [offset, setOffset] = useState(0)
  const limit = 20

  const [selected, setSelected] = useState<AdminUser | null>(null)
  const [draft, setDraft] = useState<EditDraft | null>(null)
  const [detail, setDetail] = useState<AdminUser | null>(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  const totalPages = useMemo(() => Math.ceil(total / limit), [total, limit])
  const currentPage = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit])

  const fetchUsers = () => {
    setLoading(true)
    setError('')
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset)
    })
    if (search.trim()) params.set('search', search.trim())
    if (statusFilter) params.set('status', statusFilter)
    if (adminFilter !== 'all') params.set('is_admin', adminFilter === 'true' ? 'true' : 'false')

    requestApi<UserListResponse>(`/api/v1/admin/users?${params.toString()}`)
      .then((payload) => {
        setUsers(payload.users || [])
        setTotal(payload.total || 0)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('admin.users.error'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (!isGranted) return
    fetchUsers()
  }, [offset, isGranted])

  const handleSearch = () => {
    setOffset(0)
    fetchUsers()
  }

  const handleSelectUser = (user: AdminUser) => {
    setSelected(user)
    setDraft({
      status: user.status,
      plan: user.plan,
      is_admin: user.is_admin,
      notes: user.admin_notes || ''
    })
    setDetail(null)
    setMessage('')
    requestApi<{ user: AdminUser }>(`/api/v1/admin/users/${user.id}`)
      .then((payload) => {
        setDetail(payload.user)
      })
      .catch(() => {
        setDetail(user)
      })
  }

  const handleSave = async () => {
    if (!selected || !draft) return
    setSaving(true)
    setMessage('')
    try {
      if (draft.status !== selected.status) {
        await requestApi(`/api/v1/admin/users/${selected.id}/status`, {
          method: 'PUT',
          body: JSON.stringify({ status: draft.status })
        })
      }
      if (draft.plan !== selected.plan) {
        await requestApi(`/api/v1/admin/users/${selected.id}/plan`, {
          method: 'PUT',
          body: JSON.stringify({ plan: draft.plan })
        })
      }
      if (draft.is_admin !== selected.is_admin) {
        await requestApi(`/api/v1/admin/users/${selected.id}/admin`, {
          method: 'PUT',
          body: JSON.stringify({ is_admin: draft.is_admin })
        })
      }
      if ((draft.notes || '') !== (selected.admin_notes || '')) {
        await requestApi(`/api/v1/admin/users/${selected.id}/notes`, {
          method: 'PUT',
          body: JSON.stringify({ notes: draft.notes })
        })
      }
      setMessage(t('admin.users.saved'))
      setDetail((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          status: draft.status,
          plan: draft.plan,
          is_admin: draft.is_admin,
          admin_notes: draft.notes
        }
      })
      fetchUsers()
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t('admin.users.error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
        <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/70">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">{t('admin.users.filters')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('admin.users.placeholders.search')}
            />
            <select
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">{t('admin.users.filtersAllStatus')}</option>
              <option value="active">{t('admin.users.status.active')}</option>
              <option value="inactive">{t('admin.users.status.inactive')}</option>
              <option value="banned">{t('admin.users.status.banned')}</option>
            </select>
            <select
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={adminFilter}
              onChange={(e) => setAdminFilter(e.target.value)}
            >
              <option value="all">{t('admin.users.filtersAllAdmin')}</option>
              <option value="true">{t('admin.users.filtersAdmin')}</option>
              <option value="false">{t('admin.users.filtersNonAdmin')}</option>
            </select>
            <button
              onClick={handleSearch}
              className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
            >
              {t('admin.users.search')}
            </button>
          </div>
        </div>

        {selected && draft && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700">{t('admin.users.detailTitle')}</h3>
                  <p className="text-xs text-slate-400">{selected.username || selected.phone || selected.email || selected.id}</p>
                </div>
                <button onClick={() => setSelected(null)} className="text-xs text-slate-500 hover:text-slate-700">
                  {t('admin.users.closeEditor')}
                </button>
              </div>
              <div className="space-y-3 text-sm text-slate-600">
                <div className="flex justify-between"><span>{t('admin.users.detailLabels.userId')}</span><span className="font-semibold text-slate-800">{detail?.id || selected.id}</span></div>
                <div className="flex justify-between"><span>{t('admin.users.detailLabels.phone')}</span><span>{detail?.phone || selected.phone || '-'}</span></div>
                <div className="flex justify-between"><span>{t('admin.users.detailLabels.email')}</span><span>{detail?.email || selected.email || '-'}</span></div>
                <div className="flex justify-between"><span>{t('admin.users.detailLabels.plan')}</span><span>{detail?.plan || selected.plan}</span></div>
                <div className="flex justify-between"><span>{t('admin.users.detailLabels.status')}</span><span>{detail?.status || selected.status}</span></div>
                <div className="flex justify-between"><span>{t('admin.users.detailLabels.credits')}</span><span>{detail?.credits ?? selected.credits}</span></div>
                <div className="flex justify-between"><span>{t('admin.users.detailLabels.trial')}</span><span>{detail?.trial_credits_remaining ?? selected.trial_credits_remaining}</span></div>
                <div className="flex justify-between"><span>{t('admin.users.detailLabels.createdAt')}</span><span>{detail?.created_at || selected.created_at || '-'}</span></div>
                <div className="flex justify-between"><span>{t('admin.users.detailLabels.lastLogin')}</span><span>{detail?.last_login_at || selected.last_login_at || '-'}</span></div>
              </div>
            </div>

            <div className="border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700">{t('admin.users.editorTitle')}</h3>
                  <p className="text-xs text-slate-400">{selected.username || selected.phone || selected.email || selected.id}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.users.fields.status')}</label>
                  <select
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full"
                    value={draft.status}
                    onChange={(e) => setDraft((prev) => prev ? { ...prev, status: e.target.value } : prev)}
                  >
                    <option value="active">{t('admin.users.status.active')}</option>
                    <option value="inactive">{t('admin.users.status.inactive')}</option>
                    <option value="banned">{t('admin.users.status.banned')}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.users.fields.plan')}</label>
                  <select
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full"
                    value={draft.plan}
                    onChange={(e) => setDraft((prev) => prev ? { ...prev, plan: e.target.value } : prev)}
                  >
                    <option value="free">{t('admin.users.plan.free')}</option>
                    <option value="early_bird">{t('admin.users.plan.earlyBird')}</option>
                    <option value="standard">{t('admin.users.plan.standard')}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.users.fields.admin')}</label>
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={draft.is_admin}
                      onChange={(e) => setDraft((prev) => prev ? { ...prev, is_admin: e.target.checked } : prev)}
                    />
                    {t('admin.users.adminToggle')}
                  </label>
                </div>
                <div className="space-y-2">
                  <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.users.fields.notes')}</label>
                  <input
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full"
                    value={draft.notes}
                    onChange={(e) => setDraft((prev) => prev ? { ...prev, notes: e.target.value } : prev)}
                    placeholder={t('admin.users.placeholders.notes')}
                  />
                </div>
              </div>
              {message && <p className="mt-3 text-xs text-rose-500">{message}</p>}
              <button
                onClick={handleSave}
                disabled={saving}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? t('admin.users.saving') : t('admin.users.save')}
              </button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">{t('admin.users.listTitle')}</h3>
          {loading && <p className="text-sm text-slate-400">{t('admin.users.loading')}</p>}
          {error && <p className="text-sm text-rose-500">{error}</p>}
          {!loading && !error && (
            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('admin.users.table.user')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.users.table.plan')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.users.table.status')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.users.table.credits')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.users.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{user.username || user.phone || user.email || user.id}</div>
                        <div className="text-xs text-slate-400">{user.id}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{user.plan}</td>
                      <td className="px-4 py-3 text-slate-600">{user.status}</td>
                      <td className="px-4 py-3 text-slate-600">{user.credits}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleSelectUser(user)}
                          className="text-xs font-semibold text-primary hover:text-blue-700"
                        >
                          {t('admin.users.actions.manage')}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-sm text-slate-400" colSpan={5}>
                        {t('admin.users.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{t('admin.users.pagination').replace('{current}', String(currentPage)).replace('{total}', String(totalPages))}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50"
                >
                  {t('admin.users.prev')}
                </button>
                <button
                  onClick={() => setOffset(Math.min((totalPages - 1) * limit, offset + limit))}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50"
                >
                  {t('admin.users.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
  )
}

export const AdminUsersScreen = () => {
  const { t } = useLanguage()

  return (
    <AdminLayout title={t('admin.users.title')} description={t('admin.users.desc')}>
      <AdminUsersContent />
    </AdminLayout>
  )
}
